// /src/layout/DgaMainHeader.tsx
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useMemo, useState, useEffect, useRef } from "react";
import TopbarDatesWeather from "./TopbarDatesWeather";
import menu from "../../config/menuConfig";

// LOCAL SVG ICONS
import LinkCircleIcon from "../../components/icons/LinkCircleIcon";
import LockCircleIcon from "../../components/icons/LockCircleIcon";
import SAFlagIcon from "../../components/icons/SaudiFlagIcon";

// Auth
import { AUTH_BRIDGE } from "../../config";
import { isAuthenticated, getAuthToken, clearAuthToken } from "../../services/authService";

// Arabic only - no language switching

/* tiny chevron + search (inline) */
const ChevronDown = (p: any) => (
  <svg viewBox="0 0 24 24" width="16" height="16" {...p}>
    <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);
const IconSearch = (p: any) => (
  <svg viewBox="0 0 24 24" width="18" height="18" {...p}>
    <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M20 20l-3.5-3.5" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);

/** Decode JWT payload safely */
function decodeJwtPayload(token?: string | null): Record<string, any> | null {
  if (!token) return null;
  try {
    const [, payload] = token.split(".");
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}

/** Get a display name that works for Benefactor or Employee JWTs */
function getUserDisplayName(): string | null {
  const token = getAuthToken?.() ?? null;
  const p = decodeJwtPayload(token);
  if (!p) return null;
  return (
    p.displayName ||
    p.fullName ||
    p.name ||
    p.employee_name ||
    p.donor_name ||
    p.username ||
    p.email ||
    null
  );
}

export default function DgaMainHeader() {
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [monoMode, setMonoMode] = useState(() => {
    // Load from localStorage on mount
    if (typeof window !== 'undefined') {
      return localStorage.getItem('a11y_mono_mode') === 'enabled';
    }
    return false;
  });
  const navigate = useNavigate();

  const [authed, setAuthed] = useState(() => isAuthenticated?.() ?? !!getAuthToken?.());
  const userName = useMemo(() => (authed ? getUserDisplayName() : null), [authed]);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userBtnRef = useRef<HTMLButtonElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  // Sync header with auth (login/logout in same tab + storage change in other tabs)
  useEffect(() => {
    const syncAuth = () => setAuthed(isAuthenticated?.() ?? !!getAuthToken?.());
    const eventName = AUTH_BRIDGE?.authChangedEventName ?? "auth:state-changed";
    window.addEventListener(eventName, syncAuth);
    const onStorage = (e: StorageEvent) => {
      if (e.key === "authToken") syncAuth();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(eventName, syncAuth);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // ✅ Hooks must be inside the component
  const location = useLocation();
  useEffect(() => {
    const qsLng = new URLSearchParams(location.search).get("lng") as "ar" | "en" | null;
    const stored = localStorage.getItem("i18nextLng") as "ar" | "en" | null;
    const active = qsLng || stored || "ar";
    document.documentElement.lang = active;
    document.documentElement.dir = active === "ar" ? "rtl" : "ltr";
  }, [location.search]);

  // Load mono (white/gray) mode on mount and when state changes
  useEffect(() => {
    const saved = localStorage.getItem('a11y_mono_mode') === 'enabled';
    const enabled = saved || monoMode;

    if (enabled) {
      document.body.classList.add('a11y-mono-mode');
      document.documentElement.classList.add('a11y-mono-mode');
      document.body.setAttribute('data-a11y-mono', 'enabled');
    } else {
      document.body.classList.remove('a11y-mono-mode');
      document.documentElement.classList.remove('a11y-mono-mode');
      document.body.removeAttribute('data-a11y-mono');
    }
  }, [monoMode]);

  // close on outside click / Esc
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (userBtnRef.current?.contains(t) || userMenuRef.current?.contains(t)) return;
      setUserMenuOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setUserMenuOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [userMenuOpen]);

  const handleLogout = () => {
    clearAuthToken?.();
    navigate("/benefaction/login");
  };

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* === Row 1 (utility strip) ===================================== */}
        <div className="flex items-center justify-between h-10 text-sm text-gray-600">
          <div className="flex items-center gap-4">
            <button 
              type="button"
              onClick={() => {
                const newState = !monoMode;
                setMonoMode(newState);

                if (newState) {
                  document.body.classList.add('a11y-mono-mode');
                  document.documentElement.classList.add('a11y-mono-mode');
                  document.body.setAttribute('data-a11y-mono', 'enabled');
                  localStorage.setItem('a11y_mono_mode', 'enabled');
                } else {
                  document.body.classList.remove('a11y-mono-mode');
                  document.documentElement.classList.remove('a11y-mono-mode');
                  document.body.removeAttribute('data-a11y-mono');
                  localStorage.removeItem('a11y_mono_mode');
                }
              }}
              className={`inline-flex items-center gap-1 hover:text-sa-700 transition-colors ${
                monoMode ? 'text-sa-700 font-semibold' : ''
              }`}
              title={monoMode ? 'إيقاف وضع أبيض/رمادي' : 'تفعيل وضع أبيض/رمادي'}
              aria-label={monoMode ? 'إيقاف وضع أبيض/رمادي' : 'تفعيل وضع أبيض/رمادي'}
              aria-pressed={monoMode}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              <span className="hidden sm:inline">عرض</span>
            </button>
            <button
              id="search-toggle-btn"
              className="inline-flex items-center gap-1 hover:text-sa-700"
              type="button"
              onClick={() => setSearchOpen((s) => !s)}
            >
              <IconSearch /> <span className="hidden sm:inline">بحث</span>
            </button>
            <button 
              type="button"
              onClick={() => {
                // Share functionality
                if (navigator.share) {
                  navigator.share({
                    title: document.title,
                    url: window.location.href
                  }).catch(() => {});
                } else {
                  // Fallback: copy to clipboard
                  navigator.clipboard.writeText(window.location.href).then(() => {
                    alert('تم نسخ الرابط إلى الحافظة');
                  }).catch(() => {});
                }
              }}
              className="inline-flex items-center gap-1 hover:text-sa-700"
              title="مشاركة الصفحة"
              aria-label="مشاركة الصفحة"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="18" cy="5" r="3"/>
                <circle cx="6" cy="12" r="3"/>
                <circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
              <span className="hidden sm:inline">مشاركة</span>
            </button>
          </div>

          <div className="inline-flex items-center gap-3">
            <button
              type="button"
              // Language switching removed - Arabic only
              onClick={() => {}}
              className="inline-flex items-center gap-2 hover:text-sa-800"
            >
              <span className="text-xs px-1.5 py-0.5 rounded-full border border-gray-300">A</span>
              English
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              // Language switching removed - Arabic only
              onClick={() => {}}
              className="inline-flex items-center gap-2 hover:text-sa-800"
            >
              العربية
            </button>
          </div>
        </div>

        <div className="h-px bg-gray-100" />

        {/* === Row 2 (verify + dates/weather) ============================ */}
        <div className="py-2">
          <div className="flex flex-wrap items-center justify-between gap-3 text-[13px] text-gray-700">
            <div className="flex items-center gap-2">
              <SAFlagIcon
                size={20}
                className="text-[#006923] rounded-[3px] overflow-hidden"
                title="علم المملكة العربية السعودية"
              />
              <span className="text-neutral-700 font-[600]">
                موقع حكومي رسمي تابع لحكومة المملكة العربية السعودية
              </span>
              <button
                id="topbar-toggle-btn"
                type="button"
                className="text-sa-700 text-md font-[700] hover:text-sa-600 cursor-pointer ps-2 rounded transition-colors inline-flex items-center gap-1"
                aria-expanded={verifyOpen}
                aria-controls="topbar_details_panel"
                onClick={() => setVerifyOpen((v) => !v)}
              >
                كيف تتحقق ؟
                <ChevronDown className={`transition-transform ${verifyOpen ? "rotate-180" : ""}`} />
              </button>
            </div>

            <TopbarDatesWeather />
          </div>

          {/* VERIFY COLLAPSE */}
          <div
            id="topbar_details_panel"
            className={`overflow-hidden transition-all duration-200 ${
              verifyOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <div className="flex items-center flex-wrap gap-8 pt-3 mt-3 pb-2 border-t border-gray-200">
              {/* .gov.sa block */}
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-4">
                  <div className="flex mt-2 items-center justify-center">
                    <LinkCircleIcon size={40} className="text-sa-700 drop-shadow-sm" />
                  </div>
                  <div className="text-xl font-[700] text-neutral-800">
                    روابط المواقع الالكترونية الرسمية السعودية تنتهي بـ{" "}
                    <strong className="text-sa-600">.gov.sa</strong>
                  </div>
                </div>
                <div className="text-lg text-neutral-600 ps-14">
                  جميع روابط المواقع الرسمية التابعة للجهات الحكومية في المملكة العربية السعودية تنتهي بـ .gov.sa
                </div>
              </div>

              {/* HTTPS block */}
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-4">
                  <div className="flex mt-2 items-center justify-center">
                    <LockCircleIcon size={40} className="text-sa-700 drop-shadow-sm" />
                  </div>
                  <div className="text-xl font-[700] text-neutral-800">
                    المواقع الالكترونية الحكومية تستخدم بروتوكول HTTPS للتشفير و الأمان.
                  </div>
                </div>
                <div className="text-lg text-neutral-600 ps-14">
                  المواقع الالكترونية الآمنة في المملكة العربية السعودية تستخدم بروتوكول HTTPS للتشفير.
                </div>
              </div>
            </div>

            <div className="pb-3">
              <div className="hidden md:flex items-center gap-3 px-4 py-3 rounded-lg border bg-emerald-50 text-emerald-700 border-emerald-300 shadow-sm">
                <div className="flex-shrink-0">
                  <img src="/images/logo.jpg" alt="MOIA" className="h-6 w-6 object-contain rounded-sm" />
                </div>
                <div className="flex-1">
                  <div className="text-md font-[700]">
                    مسجل لدى هيئة الحكومة الرقمية برقم :{" "}
                    <span style={{ textDecoration: "underline" }}>
                      <a
                        href="https://raqmi.dga.gov.sa/platforms/platforms/cd54803f-e8f4-4bb3-3773-08de46ba153b/platform-license"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        20260122317
                      </a>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* /VERIFY COLLAPSE */}
        </div>

        {/* === Row 3 (Brand + PRIMARY NAV — JSON) ======================= */}
        <div className="flex justify-between gap-3">
          {/* Branding */}
          <div className="site-branding flex items-center py-2.5">
            <a href={menu.branding.homeUrl} className="flex items-center">
              <img
                src={menu.branding.logoSrc || "/images/logo.jpg"}
                alt={menu.branding.logoAlt || "وزارة الشؤون الإسلامية"}
                className="site-logo h-12 w-auto object-contain"
                loading="eager"
              />
            </a>
          </div>

          {/* Primary Navigation from JSON */}
          <div className="hidden md:flex flex-1">
            <nav className="flex-1 flex primary-navigation" id="primary-nav">
              {menu.items.map((item) => (
                <div key={item.title} className="relative group mega-menu-item">
                  <a
                    href="#"
                    className="flex text-md transition-colors hover:duration-50 duration-300 ease-in-out px-3.5 py-1.5 font-[600] group min-h-full rounded-md relative hover:text-neutral-800 hover:bg-gray-100 text-neutral-700 mega-menu-trigger"
                    data-has-dropdown="true"
                  >
                    <div className="flex items-center gap-1">
                      {item.title}
                      <i className="hgi-stroke hgi-arrow-down-01 text-md transition-transform duration-200 mega-menu-arrow" />
                      <div className="absolute h-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bottom-0 rounded-full inset-x-3 bg-gray-300" />
                    </div>
                  </a>

                  {item.columns?.length ? (
                    <div className="mega-menu-dropdown bg-white border-t border-gray-200 shadow-lg hidden group-hover:block">
                      <div className="mega-menu-content">
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                          {item.columns.map((col) => (
                            <div key={col.heading} className="mega-menu-column">
                              <a
                                href="#"
                                className="block text-lg font-semibold text-gray-800 hover:text-gray-900 transition-colors duration-200 mb-3 group"
                              >
                                <span className="group-hover:ps-1 inline-block transition-padding duration-200">
                                  {col.heading}
                                </span>
                              </a>
                              <ul className="space-y-1.5">
                                {(col.links || []).map((lnk) => (
                                  <li key={lnk.label}>
                                    <a
                                      href={lnk.url}
                                      className="text-md font-[500] text-gray-500 hover:text-gray-800 transition-all duration-200 flex items-center gap-2 py-1 group/item"
                                    >
                                      <span className="group-hover/item:ps-1 transition-padding duration-200">
                                        {lnk.label}
                                      </span>
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </nav>

            {/* Right strip: Search + Account */}
            <div className="flex">
              {/* Search Button */}
              <div className="flex search-menu-item">
                {/* Search Popover */}
                <div
                  id="search-popover"
                  className={
                    "search-dropdown bg-white border-t border-gray-200 shadow-lg " +
                    (searchOpen ? "" : "hidden")
                  }
                >
                  <div className="search-content">
                    <div className="max-w-7xl mx-auto pt-14 pb-10 px-4 md:px-6">
                      {/* Search Form */}
                      <div className="mb-8">
                        <form id="site-search-form" action={menu.search.action} className="relative flex gap-2" method="GET">
                          <div className="relative flex-1">
                            <button
                              type="submit"
                              className="absolute inset-y-0 start-0 flex items-center justify-center w-15 text-gray-400 hover:text-sa-600 transition-colors"
                            >
                              <i className="hgi-stroke hgi-search-01 text-xl" />
                            </button>
                            <input
                              type="search"
                              id="search-input"
                              name="s"
                              placeholder="ابدأ البحث..."
                              className="w-full px-4 py-2 ps-12 text-lg border-2 border-gray-300 rounded-md focus:border-gray-500 focus:ring-2 focus:ring-gray-200 focus:outline-none transition-colors"
                              autoComplete="off"
                            />
                          </div>
                          <button
                            type="submit"
                            className="cursor-pointer flex items-center bg-sa-600 hover:bg-sa-500 text-white font-[500] px-4 py-2 rounded-md transition-colors"
                          >
                            بحث
                          </button>
                        </form>
                      </div>

                      {/* Search Suggestions */}
                      <div className="search-suggestions">
                        <h3 className="text-lg font-[600] text-sa-600 mb-3">اقتراحات للبحث</h3>
                        <ul className="flex gap-2">
                          {menu.search.suggestions.map((sug) => (
                            <li key={sug.label}>
                              <a
                                href={sug.url}
                                className="bg-gray-100 hover:bg-gray-200 px-3 rounded-md text-md text-gray-700 font-[500] group transition-colors flex items-center gap-2 py-1 group"
                              >
                                <i className="hgi-stroke hgi-add-01 opacity-90 group-hover:opacity-100" />
                                <span className="transition-all duration-200">{sug.label}</span>
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Dropdown */}
              <div className="flex">
                {/* make sure the anchor is the positioning context and doesn't clip */}
                <div className="relative flex overflow-visible">
                  <button
                    ref={userBtnRef}
                    type="button"
                    onClick={() => setUserMenuOpen(v => !v)}
                    aria-haspopup="menu"
                    aria-expanded={userMenuOpen}
                    aria-controls="user-menu"
                    className="inline-flex items-center gap-1.5 rounded-md border border-black/10 bg-white px-2.5 py-1.5
                              text-sa-800 hover:bg-gray-50 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-sa-500"
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" className="shrink-0">
                      <circle cx="12" cy="8" r="3.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M4.5 19c0-3.7 3.8-5.6 7.5-5.6S19.5 15.3 19.5 19" fill="none" stroke="currentColor" strokeWidth="1.8" />
                    </svg>
                    <ChevronDown className={`opacity-70 transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
                    <span className="sr-only">القائمة الشخصية</span>
                  </button>

                  {/* Menu panel */}
                  <div
                    id="user-menu"
                    ref={userMenuRef}
                    role="menu"
                    aria-label="القائمة الشخصية"
                    className={[
                      "absolute top-full right-0 left-auto mt-2",        // <-- anchor to right in all locales
                      "w-64 max-w-[calc(100vw-1rem)]",                   // keep inside viewport on small screens
                      "bg-white border border-gray-200 rounded-lg shadow-lg z-[1000]",
                      userMenuOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-1",
                      "transition-all duration-200"
                    ].join(" ")}

                  >
                    <div className="py-2">
                      {authed ? (
                        <>
                          <button
                            role="menuitem"
                            onClick={() => { setUserMenuOpen(false); navigate("/benefaction/benefactors"); }}
                            className="w-full text-right flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md transition-colors"
                          >
                            <i className="hgi-stroke hgi-users-01 text-[16px]" />
                            قائمة المستفيدين
                          </button>

                          <div className="my-1 h-px bg-black/5" />

                          <button
                            role="menuitem"
                            onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                            className="w-full text-right flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 rounded-md transition-colors"
                          >
                            <i className="hgi-stroke hgi-logout-01 text-[16px]" />
                            تسجيل الخروج
                          </button>
                        </>
                      ) : (
                        <button
                          role="menuitem"
                          onClick={() => { setUserMenuOpen(false); navigate("/benefaction/login"); }}
                          className="w-full text-right flex items-center gap-3 px-4 py-2 text-sm text-sa-700 hover:bg-sa-50 hover:text-sa-800 rounded-md transition-colors"
                        >
                          <i className="hgi-stroke hgi-lock-01 text-[16px]" />
                          تسجيل الدخول
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* /Account */}

            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              type="button"
              id="mobile-menu-button"
              className="mobile-menu-toggle inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-300 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sa-400 transition-colors"
              aria-controls="mobile-menu"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((o) => !o)}
            >
              <span className="sr-only">Open main menu</span>
              <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        <div className={"md:hidden " + (mobileOpen ? "" : "hidden")} id="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-gray-900 border-t border-gray-800">
            {menu.items.map((it) => (
              <div key={it.title}>
                <div className="text-gray-200 font-[700] pt-2">{it.title}</div>
                {(it.columns || []).map((col) => (
                  <div key={col.heading} className="mt-1">
                    <div className="text-gray-400 text-xs">{col.heading}</div>
                    <ul>
                      {(col.links || []).map((lnk) => (
                        <li key={lnk.label}>
                          <a href={lnk.url} className="block text-right text-gray-100 px-3 py-1 rounded hover:bg-gray-800">
                            {lnk.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}

            {/* Language Switcher (mobile) */}
            <div className="language-switcher pt-4 border-t border-gray-800 mt-4">
              <span className="block text-sm font-medium text-gray-300 mb-2">Language</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    // Language switching removed - Arabic only
                    setMobileOpen(false);
                  }}
                  className="px-3 py-1.5 rounded-md border border-gray-700 text-gray-200 hover:bg-gray-800"
                >
                  English
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Language switching removed - Arabic only
                    setMobileOpen(false);
                  }}
                  className="px-3 py-1.5 rounded-md border border-gray-700 text-gray-200 hover:bg-gray-800"
                >
                  العربية
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* /Mobile panel */}
      </div>

      {/* Thin accent */}
      <div className="h-[3px] w-full bg-sa-700" />
    </header>
  );
}
